import { DateUtils, useIsMobile } from '@brunommsilva/react-foundations';
import {
  CardsList,
  Currency,
  EmptyState,
  Table,
} from '@brunommsilva/react-foundations';
import { HelpOutlined } from '@mui/icons-material';
import { Box as MuiBox, IconButton, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CATEGORY_ICON_MAP } from '../../config/category-icons';
import { TEST_IDS } from '../../config/test-ids';
import { Transaction } from '../../models/transactions';
import { Copy, Delete, Edit, SearchOff } from '../icons';
import getCurrencyColor from './currency-color';
import CopyTransactionModal from './modals/copy-transaction-modal';
import DeleteTransactionModal from './modals/delete-transaction-modal';
import EditTransactionModal from './modals/edit-transaction-modal';
import TransferAccounts from './transfer-accounts';

interface TransactionsListProps {
  transactions: Transaction[];
  isLoading: boolean;
  isError?: boolean;
}

const TransactionsList = ({
  transactions,
  isLoading,
  isError,
}: TransactionsListProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [currentTransaction, setCurrentTransaction] = useState<Transaction>();
  const [showDeleteTransactionModal, setShowDeleteTransactionModal] =
    useState(false);
  const [showEditTransactionModal, setShowEditTransactionModal] =
    useState(false);
  const [showCopyTransactionModal, setShowCopyTransactionModal] =
    useState(false);

  const onDeleteTransaction = (transaction: Transaction) => {
    setCurrentTransaction(transaction);
    setShowDeleteTransactionModal(true);
  };

  const closeDeleteTransactionModal = () => {
    setShowDeleteTransactionModal(false);
  };

  const onEditTransaction = (transaction: Transaction) => {
    setCurrentTransaction(transaction);
    setShowEditTransactionModal(true);
  };

  const closeEditTransactionModal = () => {
    setShowEditTransactionModal(false);
  };

  const onCopyTransaction = (transaction: Transaction) => {
    setCurrentTransaction(transaction);
    setShowCopyTransactionModal(true);
  };

  const closeCopyTransactionModal = () => {
    setShowCopyTransactionModal(false);
  };

  const buildColumns = () => {
    return [
      {
        id: 'DATE',
        label: t('TRANSACTION_DATE'),
        format: (transaction: Transaction) => (
          <span style={{ whiteSpace: 'nowrap' }}>
            {DateUtils.formatDate(transaction.date)}
          </span>
        ),
      },
      {
        id: 'ACCOUNT',
        label: t('TRANSACTION_ACCOUNTS'),
        format: (transaction: Transaction) => {
          if (transaction.type === 'TRANSFER') {
            return (
              <TransferAccounts
                from={transaction.account.name}
                to={transaction.otherAccount?.name}
              />
            );
          }
          return transaction.account.name;
        },
      },
      {
        id: 'AMOUNT',
        label: t('TRANSACTION_AMOUNT'),
        align: 'right',
        format: (transaction: Transaction) => (
          <span data-testid={TEST_IDS.transactions.amount}>
            <Currency
              amount={transaction.amount}
              color={getCurrencyColor(transaction.type)}
            />
          </span>
        ),
      },
      {
        id: 'CATEGORY',
        label: t('TRANSACTION_CATEGORY'),
        format: (transaction: Transaction) => {
          const IconComp =
            CATEGORY_ICON_MAP[transaction.category.icon || 'other'] ||
            HelpOutlined;
          if (isMobile) {
            return (
              <MuiBox
                sx={{
                  display: 'inline-flex',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <IconComp sx={{ color: 'text.secondary', fontSize: 20 }} />
              </MuiBox>
            );
          }
          return (
            <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconComp sx={{ color: 'text.secondary', fontSize: 20 }} />
              <span data-testid={TEST_IDS.transactions.category}>
                {transaction.category.name}
              </span>
            </MuiBox>
          );
        },
      },
      {
        id: 'DESCRIPTION',
        label: t('TRANSACTION_DESCRIPTION'),
        format: (transaction: Transaction) => (
          <span data-testid={TEST_IDS.transactions.description}>
            {transaction.description || '-'}
          </span>
        ),
      },
      {
        id: 'ACTIONS',
        format: (transaction: Transaction) => (
          <Stack
            direction="row"
            sx={{
              gap: 1,
            }}
          >
            <IconButton
              color="secondary"
              onClick={() => onCopyTransaction(transaction)}
            >
              <Copy />
            </IconButton>
            <IconButton
              color="primary"
              onClick={() => onEditTransaction(transaction)}
            >
              <Edit />
            </IconButton>
            <IconButton
              color="error"
              data-testid={TEST_IDS.transactions.deleteButton}
              onClick={() => onDeleteTransaction(transaction)}
            >
              <Delete />
            </IconButton>
          </Stack>
        ),
      },
    ];
  };

  const renderDeleteTransactionModal = () => {
    return (
      <DeleteTransactionModal
        show={showDeleteTransactionModal}
        transaction={currentTransaction}
        onDeleted={closeDeleteTransactionModal}
        onClose={closeDeleteTransactionModal}
      />
    );
  };

  const renderEditTransactionModal = () => {
    return (
      <EditTransactionModal
        show={showEditTransactionModal}
        transaction={currentTransaction}
        onEdited={closeEditTransactionModal}
        onClose={closeEditTransactionModal}
      />
    );
  };

  const renderCopyTransactionModal = () => {
    return (
      <CopyTransactionModal
        show={showCopyTransactionModal}
        transaction={currentTransaction}
        onCopied={closeCopyTransactionModal}
        onClose={closeCopyTransactionModal}
      />
    );
  };

  const renderEmpty = () => (
    <EmptyState
      title={t('NO_TRANSACTIONS_FOUND_MESSAGE')}
      Icon={SearchOff}
      height={300}
    />
  );

  const renderCards = () => (
    <CardsList
      isLoading={isLoading}
      isError={isError}
      items={transactions}
      renderItem={(transaction) => {
        const isTransfer = transaction.type === 'TRANSFER';
        const IconComp =
          CATEGORY_ICON_MAP[transaction.category.icon || 'other'] ||
          HelpOutlined;

        return (
          <MuiBox
            key={transaction.id}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              backgroundColor: 'background.paper',
            }}
          >
            {/* Left Column */}
            <MuiBox sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
              <IconComp
                sx={{ color: 'text.secondary', fontSize: 24, mt: 0.5 }}
              />
              <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MuiBox>
                  <Typography
                    variant="body1"
                    component="div"
                    sx={{
                      fontWeight: 'bold',
                      lineHeight: 1.2,
                    }}
                  >
                    {isTransfer ? (
                      <TransferAccounts
                        from={transaction.account.name}
                        to={transaction.otherAccount?.name}
                      />
                    ) : (
                      transaction.category.name
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {isTransfer && `${transaction.category.name} · `}
                    {DateUtils.formatDate(transaction.date)}
                  </Typography>
                </MuiBox>
                <MuiBox>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    {!isTransfer && transaction.account.name}
                  </Typography>
                  {transaction.description && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        mt: 0.25,
                      }}
                    >
                      "{transaction.description}"
                    </Typography>
                  )}
                </MuiBox>
              </MuiBox>
            </MuiBox>

            {/* Right Column */}
            <MuiBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                minWidth: '80px',
                ml: 1,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                }}
              >
                <Currency
                  amount={transaction.amount}
                  color={getCurrencyColor(transaction.type)}
                />
              </Typography>
              <MuiBox sx={{ display: 'flex', gap: 0.5, mb: -0.5, mr: -0.5 }}>
                <IconButton
                  size="small"
                  color="secondary"
                  onClick={() => onCopyTransaction(transaction)}
                >
                  <Copy fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onEditTransaction(transaction)}
                >
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onDeleteTransaction(transaction)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </MuiBox>
            </MuiBox>
          </MuiBox>
        );
      }}
    />
  );

  const renderTable = () => (
    <Table
      isLoading={isLoading}
      isError={isError}
      columns={buildColumns()}
      rows={transactions}
    />
  );

  const renderList = () => {
    if (!isLoading && !isError && !transactions?.length) {
      return renderEmpty();
    }
    if (isMobile) {
      return renderCards();
    }
    return renderTable();
  };

  return (
    <>
      {renderList()}
      {renderDeleteTransactionModal()}
      {renderEditTransactionModal()}
      {renderCopyTransactionModal()}
    </>
  );
};

export default TransactionsList;
